import { CompositeSymbolDc } from "@evergis/api/dist/__generated__/data-contracts";
import { RangeValue } from "@evergis/ui";
import { ClusterSymbolDc, H3GridSymbolDc, SvgPointSymbolDc } from "@evergis/api";

import { CLUSTER_STROKE_OPACITY } from "../modules/Style/constants";
import { DEFAULT_ID_ATTRIBUTE_NAME } from "../evergis/constants";
import { castResolutionToScale } from "../evergis/core/scale";
import { colorAlpha } from "./color";
import { extractSymbol, getChildSymbols } from "../evergis/core/symbol/compositeSymbol";
import { getParameterValue, isParameterByAttribute } from "../evergis/core/classification/getFromClassified";
import { Style, StyleState } from "../ducks/style/style";
import {
  ClassifyAble,
  ClusterSymbol,
  CompositeSymbolType,
  isClusterSymbol,
  isCompositeSymbol,
  isSimpleSymbol,
  LabelType,
  ParameterByAttribute,
  ParameterValue,
  StyleSymbolType,
} from "../interfaces/style";

export const getSegmentIndex = ({ scaleSegments, selectedSegment }: StyleState): number =>
  scaleSegments?.findIndex(segment => segment === selectedSegment);

export const getStyleChildIndex = (
  children: Style[],
  selectedSegment: number,
  defaultScaleRange: RangeValue,
): number => {
  return children?.findIndex(({ minResolution, maxResolution }) => {
    const scale = castResolutionToScale({ minResolution, maxResolution }, defaultScaleRange);

    return selectedSegment === scale[0];
  });
};

export const getStyleLabel = (symbol: StyleSymbolType): LabelType => {
  if (isClusterSymbol(symbol)) {
    return (symbol as ClusterSymbolDc).labelSymbol as LabelType;
  }

  return extractSymbol(symbol as CompositeSymbolDc)?.label as LabelType;
};

export const updateSpecSymbol = (
  changedSymbol: Partial<StyleSymbolType>,
  symbol: StyleSymbolType,
): Partial<StyleSymbolType> => {
  let newSymbol: Partial<StyleSymbolType> | null;

  if (isClusterSymbol(symbol as ClusterSymbol)) {
    const isClassifiedFillColor = isParameterByAttribute((symbol as ClusterSymbolDc).fillColor);
    const fillColor = getParameterValue(
      ((changedSymbol as SvgPointSymbolDc).background?.fillColor as ClassifyAble<string>) ||
        ((symbol as ClusterSymbolDc).fillColor as ClassifyAble<string>),
    );

    newSymbol = {
      ...symbol,
      fillColor: isClassifiedFillColor ? (symbol as ClusterSymbolDc).fillColor : fillColor,
      strokeColor: colorAlpha(fillColor, CLUSTER_STROKE_OPACITY),
      singleObjectSymbol: changedSymbol,
    } as Partial<StyleSymbolType>;
  } else if (isCompositeSymbol(symbol as CompositeSymbolType)) {
    const childSymbols = getChildSymbols(symbol) as StyleSymbolType[];
    const index = childSymbols.findIndex(isSimpleSymbol);

    childSymbols[index] = { ...childSymbols[index], ...changedSymbol } as StyleSymbolType;

    newSymbol = { ...symbol, childSymbols } as StyleSymbolType;
  } else {
    newSymbol = { ...symbol, ...changedSymbol } as StyleSymbolType;
  }

  return newSymbol as Partial<StyleSymbolType>;
};

export const getExtractedSymbol = (symbol: StyleSymbolType, extractCluster = false): StyleSymbolType => {
  const extracted = isCompositeSymbol(symbol as CompositeSymbolType) ? extractSymbol(symbol)?.symbol : symbol;

  return extractCluster && isClusterSymbol(extracted as ClusterSymbol)
    ? ((extracted as ClusterSymbol).singleObjectSymbol as StyleSymbolType)
    : extracted;
};

export const updateIdAttributeOfH3Symbol = (symbol: H3GridSymbolDc, idAttribute: string) => {
  if (idAttribute === DEFAULT_ID_ATTRIBUTE_NAME) return symbol;

  const newSymbol = { ...symbol };

  (newSymbol.fill.color as ParameterByAttribute<ParameterValue>).values = (
    newSymbol.fill.color as ParameterByAttribute<ParameterValue>
  ).values.map(value => ({
    ...value,
    condition: value.condition.replaceAll(DEFAULT_ID_ATTRIBUTE_NAME, idAttribute),
  }));

  newSymbol.aggregations = newSymbol.aggregations.map(aggregation =>
    aggregation.attributeName === DEFAULT_ID_ATTRIBUTE_NAME
      ? { ...aggregation, attributeName: idAttribute }
      : aggregation,
  );

  return newSymbol;
};
